import { faAward } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import api, { API_ENDPOINT } from '@/api';
import type { components } from '@/api/qraft/schema';
import { CertificateCards } from '@/common/CertificateCards';
import { TitleWithQuestion } from '@/common/TitleWithQuestion';
import { WBSButton } from '@/common/WBSButton';
import { useCourseStudyAccess } from '~/hooks/useCourseStudyAccess';
import { GroupHeaderWrapper } from '~/styles/layoutElements';

type CertificateSummary = components['schemas']['CertificateSummary'];

export const Route = createFileRoute(
  '/_app/_users/members/$memberId/course-study/certificates',
)({
  component: CertificatesScreen,
});

function CertificatesScreen() {
  const { t } = useTranslation();
  const { memberId } = Route.useParams();
  const memberIdNum = Number.parseInt(memberId, 10);
  const { access } = useCourseStudyAccess(memberId);

  // One call for both the self and the supervisory case: the subject is the
  // path, so a study helper opening their own tab no longer sends no `user_id`
  // and gets a 400. A SELF caller receives no pending rows at all.
  // `limit` because legacy's tab is a plain unpaginated list
  // (`buddypress/members/single/course-study/certificates.php:11-20`) and the
  // default page of 10 is one row clear of today's busiest holder (measured
  // 2026-08-16: max 9 rows per user in `user_certificates`).
  const { data: certificates, refetch } = api.listStudentCertificates.useQuery(
    { path: { student_id: memberIdNum }, query: { limit: 50 } },
    { enabled: Number.isFinite(memberIdNum) },
  );

  const { mutateAsync: award, isPending: isAwarding } =
    api.awardCertificate.useMutation();

  const all = certificates?.data ?? [];
  const awarded = all.filter((c) => !c.is_pending);
  const pending = all.filter((c) => c.is_pending);

  const hasCertificates = awarded.length > 0 || pending.length > 0;

  const handleAward = async (cert: CertificateSummary) => {
    await award({
      body: {
        user_id: cert.user_id,
        course_id: cert.course_id,
        series_id: cert.series_id,
      },
    });
    await refetch();
  };

  return (
    <>
      <GroupHeaderWrapper>
        <TitleWithQuestion title={t('wbs.certificates')} />
      </GroupHeaderWrapper>
      <InfoText>{t('forum.certificates-are-achieved-by')}</InfoText>
      {hasCertificates ? (
        <>
          {awarded.length > 0 && (
            <CertificateCards
              certificates={awarded.map((c) => ({
                id: c.id,
                // The CERTIFICATE post title, not the course's — legacy prints
                // `$certificate->post_title` (`certificates.php:14`), which
                // reads "The Way" for course 26282 "The Way to Life".
                title: c.template_title,
                // Root-relative on the wire so the response body stays free of
                // the WordPress passthrough URL; the endpoint 302s to it.
                downloadUrl: c.download_url
                  ? `${API_ENDPOINT}${c.download_url}`
                  : undefined,
              }))}
            />
          )}
          {/* The server's own answer, not a client re-derivation of it: the two
              agree today (every viewer who can reach this tab and see a pending
              row is a study helper or an admin), but `!isStudent` is one new
              follow-up-coordinator group admin away from rendering a button the
              award route refuses. */}
          {access?.can_award === true && pending.length > 0 && (
            <PendingSection>
              <PendingHeading>{t('wbs.pending')}</PendingHeading>
              <PendingList>
                {pending.map((cert) => (
                  <PendingRow key={cert.id} data-testid="pending-certificate">
                    <PendingTitle>{cert.template_title}</PendingTitle>
                    <WBSButton
                      disabled={isAwarding}
                      onClick={() => handleAward(cert)}
                    >
                      {t('wbs.assign-certificate-2')}
                    </WBSButton>
                  </PendingRow>
                ))}
              </PendingList>
            </PendingSection>
          )}
        </>
      ) : (
        <NoCertificatesWrapper>
          <IconWrapper>
            <NoAwardsIcon icon={faAward} />
          </IconWrapper>
          <NoCertText>{t('lessons.you-currently-do-not')}</NoCertText>
        </NoCertificatesWrapper>
      )}
    </>
  );
}

const NoCertificatesWrapper = styled.div`
  align-items: center;
  border-radius: 5px;
  color: var(--grey-600);
  display: flex;
  flex-direction: column;
  font-family: var(--raleway);
  font-size: 16px;
  padding: 100px 0;
`;

const InfoText = styled.p`
  color: var(--grey-900);
  font-size: 16px;
  line-height: 1.6;
  margin: 0;
`;

const NoAwardsIcon = styled(FontAwesomeIcon)`
  color: var(--white);
  font-size: 100px;
`;

const NoCertText = styled.p`
  color: var(--grey-600);
  font-size: 16px;
  margin-top: 30px;
  max-width: 1000px;
  text-align: center;
`;
const IconWrapper = styled.div`
  align-items: center;
  background-color: var(--grey-300);
  border-radius: 50%;
  box-shadow: var(--shadow-inset-md);
  display: flex;
  height: 190px;
  justify-content: center;
  width: 190px;
`;

const PendingSection = styled.div`
  margin-top: 40px;
  max-width: 810px;
`;

const PendingHeading = styled.h3`
  color: var(--black);
  font-family: var(--raleway);
  font-size: 18px;
  margin: 0 0 16px;
`;

const PendingList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const PendingRow = styled.div`
  align-items: center;
  background: var(--white);
  border-radius: 5px;
  box-shadow: var(--shadow-sm);
  display: flex;
  gap: 16px;
  justify-content: space-between;
  padding: 16px 20px;
`;

const PendingTitle = styled.div`
  color: var(--black);
  font-family: var(--raleway);
  font-size: 16px;
`;
